import Autocomplete from '@mui/material/Autocomplete';
import { Grid, TextField } from '@mui/material';

type Props = {
    grid?: boolean;
    name: string;
    caption: string;
    options: Record<string, unknown>[];
    defVal: unknown;
    size?: 'small' | 'medium';
    variant?: 'outlined' | 'filled' | 'standard';
    labelField?: string;
    valueField?: string;
    onChange: (event: { name: string | undefined; value: unknown }) => void;
    [key: string]: unknown;
};

/*
 * SttSelectAutocomplete: render a generic Autocomplete Select
 * bool grid: wrap the item in a grid element, take xs, sm, md etc. from ...rest props
 * string name: field name,
 * string caption: textarea label
 * array options: list of arbitrary option objects
 * string defVal: value of the currently selected option
 * string labelField: which option field to use for the option labels, for example {"name"}
 * string valueField: which option field to use for the values, for example {"id"}
 * function onChange: callback for the select
 * */
export function SttSelectAutocomplete({
    grid,
    name,
    caption,
    options,
    defVal,
    size = 'small',
    variant = 'outlined',
    labelField = 'name',
    valueField = 'id',
    onChange,
    ...rest
}: Props): React.ReactElement {
    const selected = options.find(
        (option: any) => option[valueField] === defVal,
    ) || {
        [labelField]: '',
        [valueField]: null,
    };

    function change(item: Record<string, unknown> | null) {
        if (onChange) {
            onChange({ name, value: item ? item[valueField] : undefined });
        }
    }

    // Render item
    const content = (
        <Autocomplete
            options={options}
            getOptionLabel={(option: any) =>
                (option[labelField] as string) || ''
            }
            onChange={(_ev: any, item: any) => change(item)}
            value={selected}
            renderInput={(params: any) => (
                <TextField
                    fullWidth
                    {...params}
                    size={size}
                    label={caption}
                    variant={variant}
                />
            )}
        />
    );

    return grid ? <Grid {...rest}>{content}</Grid> : content;
}
